// Responsive breakpoint contract — the JS half. DESIGN.md ("Responsive") is the
// prose source of truth; these literals are the machine-readable copy.
//
// Two tiers, named for what they mean to THIS product rather than for devices:
//
//   medium  768-1023px  tablet portrait / split window. The SUPPORTED FLOOR.
//   wide    >= 1024px   the desktop layout, unchanged.
//
// Below 768px there is no layout guarantee — only "no catastrophic failure".
//
// Why the numbers are duplicated in CSS rather than tokenised: CSS custom
// properties CANNOT be used inside an `@media` (or `@container`) condition, so a
// `--bp-wide` token in tokens.css would be decorative — every stylesheet would
// still have to hard-code the literal. So the literals live in the CSS with a
// `/* medium */` comment (grep for that to find every responsive rule) and are
// exported once from here for the JS side.
//
// Prefer `@container appmain (...)` over `@media (...)` for anything INSIDE the
// content column: the sidebar is 260px or 56px wide depending on its state, so
// viewport width mispredicts the available width by ~200px. `.app-shell-main`
// declares `container-type: inline-size; container-name: appmain`.

import { useEffect, useState } from 'react';

/** Minimum viewport width we lay out for. Below this: best effort only. */
export const BP_MEDIUM_MIN = 768;

/** At and above this the layout is the unchanged desktop one. */
export const BP_WIDE_MIN = 1024;

/** The two supported layout tiers. A third (`compact`) would slot in below. */
export type Breakpoint = 'medium' | 'wide';

/** `wide` when the viewport is >= 1024px. The one query the app branches on. */
export const WIDE_QUERY = `(min-width: ${BP_WIDE_MIN}px)`;

/**
 * Subscribe to a media query. SSR- and test-safe: when `matchMedia` is absent
 * (jsdom ships no implementation) this reports `false` and never subscribes,
 * which lands the app on the `medium` layout — the more constrained of the two,
 * so a missing API degrades toward the safer rendering rather than the one that
 * assumes room it may not have.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Re-read on (re)subscribe: the query may have changed, or the viewport may
    // have moved between the initial render and this effect.
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** The current layout tier. */
export function useBreakpoint(): Breakpoint {
  return useMediaQuery(WIDE_QUERY) ? 'wide' : 'medium';
}

/** True while the viewport is in the `medium` (tablet) tier or narrower. */
export function useIsMedium(): boolean {
  return !useMediaQuery(WIDE_QUERY);
}
